import { Location } from '@angular/common';
import { ComponentRef, createNgModule, Injectable, Injector, NgModuleRef, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, iif, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, pluck, switchMap, take, tap } from 'rxjs/operators';

import { CalendarPickerService } from '@components/report-filters/services/calendar-picker.service';
import { EventPickerService } from '@components/report-filters/services/event-picker.service';
import { SitePickerService } from '@components/report-filters/services/site-picker.service';
import { close_active_report, select_report } from '@rpt-ngrx/actions/reports.action';
import { disable_content_loader, enable_content_loader } from '@rpt-ngrx/actions/ui.action';
import { selectEventState } from '@rpt-ngrx/selectors/events.selector';
import { selectStandaloneReportFilters } from '@rpt-ngrx/selectors/filters.selector';
import { selectAllReports, selectReportsHistory } from '@rpt-ngrx/selectors/reports.selector';
import { selectSiteState } from '@rpt-ngrx/selectors/sites.selector';
import { FiltersService } from '@services/filters';
import { LazyLoaderService } from '@services/lazy-loader.service';
import { NotificationService, ReportsService } from '@shared/services';
import { ICompany, IPublishedReport, IReport, IReportLoadingObject, ReportOptions } from '@shared/types';

@Injectable({
    providedIn: 'root',
})
export class ReportNavigatorService {
    constructor(
        private store: Store,
        private reportsService: ReportsService,
        private filterService: FiltersService,
        private lazyLoader: LazyLoaderService,
        private injector: Injector,
        private translations: TranslateService,
        private router: Router,
        private location: Location,
        private calendarPickerService: CalendarPickerService,
        private sitePickerService: SitePickerService,
        private eventPickerService: EventPickerService,
        private notificationService: NotificationService,
    ) {}

    private componentRef: ComponentRef<any>;
    private filtersRef: ComponentRef<any>;

    private currentReportViewRef: ViewContainerRef;
    private filtersViewRef: ViewContainerRef;

    private standaloneRun: boolean = false;
    private breadcrumbRun: boolean = false;
    private preparedReportParams: { [key: string]: string } = null;
    private loadedReportId: string;
    private defaultFilters: ReportOptions | { [p: string]: string };

    public bindComponentRef(ref: ComponentRef<any>): void {
        this.componentRef = ref;
    }

    public bindFiltersRef(ref: ComponentRef<any>): void {
        this.filtersRef = ref;
    }

    public bindViewRef(ref: ViewContainerRef): void {
        this.currentReportViewRef = ref;
    }

    public bindFiltersViewRef(ref: ViewContainerRef): void {
        this.filtersViewRef = ref;
    }

    public loadReport(reportLoadingData: IReportLoadingObject): void {
        if (this.loadedReportId === reportLoadingData.reportId) {
            this.store.dispatch(disable_content_loader());
            return;
        }
        let lazyModulePromise: Promise<[void]>;
        this.selectReportOptions(reportLoadingData)
            .pipe(
                catchError(() => {
                    this.store.dispatch(disable_content_loader());
                    if (!reportLoadingData.target) {
                        this.location.back();
                    }
                    return of(null);
                }),
                filter((data) => !!data),
                switchMap((report) => {
                    this.store.dispatch(disable_content_loader());
                    lazyModulePromise = Promise.all([
                        this.loadLazyReportComponent(
                            report,
                            reportLoadingData.companyId,
                            reportLoadingData.companyInfo,
                            reportLoadingData.drillDown,
                        ),
                    ]);
                    return of(report);
                }),
            )
            .subscribe((report) => {
                lazyModulePromise.then(() => {
                    const filters = this.filterService.setDefaultFilters(report, this.defaultFilters);
                    this.preparedReportParams = this.filterService.prepareParamsForCustomReports(filters, report);
                    this.store.dispatch(
                        select_report({
                            report,
                            filters,
                            isDrillDown: reportLoadingData.drillDown,
                        }),
                    );
                    if (this.preparedReportParams.is_execute_automatically || this.standaloneRun) {
                        this.reportsService.acceptFilters(this.preparedReportParams);
                        this.reportsService.loadReport = true;
                    }
                    this.componentRef.changeDetectorRef?.detectChanges();
                });
            });
    }

    private selectReportOptions(reportLoadingData: IReportLoadingObject): Observable<IReport | IPublishedReport> {
        this.store.dispatch(enable_content_loader());
        this.standaloneRun = !!reportLoadingData.target;
        this.breadcrumbRun = !!reportLoadingData.autorun;
        this.defaultFilters = reportLoadingData.reportOptions;
        return this.store.select(selectAllReports).pipe(
            pluck('reports'),
            distinctUntilChanged((prev, next) => prev.length === next.length),
            switchMap((reports: Array<IReport>) =>
                iif(
                    () => {
                        const report = reports?.find((report) => {
                            return (
                                report._id === reportLoadingData.reportId || report.id === reportLoadingData.reportId
                            );
                        });
                        return report && !reportLoadingData.target;
                    },
                    of(
                        reports.find((report) => {
                            return (
                                report._id === reportLoadingData.reportId || report.id === reportLoadingData.reportId
                            );
                        }),
                    ),
                    this.reportsService.fetchReportOptions(reportLoadingData.reportId, reportLoadingData.isDetail).pipe(
                        pluck('reportOptions'),
                        switchMap((report: IReport) =>
                            this.store.select(selectStandaloneReportFilters).pipe(
                                tap((filters) => {
                                    this.defaultFilters = filters;
                                    report.target = reportLoadingData.target;
                                    report.is_execute_automatically = true;
                                    report.custom_report_title =
                                        reportLoadingData.parentCustomTitle || report.custom_report_title;
                                }),
                                map(() => {
                                    return report;
                                }),
                            ),
                        ),
                    ),
                ),
            ),
        );
    }

    private async loadLazyReportComponent(
        report: IReport | IPublishedReport,
        companyId: string,
        companyInfo: Partial<ICompany>,
        drillDownMode: boolean,
    ): Promise<void> {
        let moduleName = '';
        let m, ReportComponent, ngModuleRef;
        let reportName = (report as IPublishedReport).parent_report_id || report.id;
        this.loadedReportId = report._id;
        const reportIdArr = reportName.split('_');
        if (this.componentRef) {
            this.componentRef = null;
            this.currentReportViewRef.clear();
            this.currentReportViewRef.remove();
        }
        if (this.filtersViewRef) {
            this.filtersViewRef.clear();
            this.filtersViewRef.remove();
        }
        switch (report.source) {
            case 'custom':
                if (reportIdArr[reportIdArr.length - 1] === 'report') {
                    reportIdArr.pop();
                    reportName = reportIdArr.join('_');
                }
                moduleName = reportName
                    .replace('/', '_')
                    .split('_')
                    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                    .join('');
                this.store.dispatch(enable_content_loader());
                m = await this.lazyLoader.dynamicImportCustomReportModule(moduleName);
                try {
                    ngModuleRef = createNgModule(m, this.injector);
                    await this.translations.use(`${moduleName}Module`).toPromise();
                    ReportComponent = m.getReportComponent();
                    this.componentRef = this.currentReportViewRef.createComponent(ReportComponent, {
                        ngModuleRef: ngModuleRef,
                    });
                    this.componentRef.instance['currentCompanyId'] = companyId;
                    this.componentRef.instance['reportName'] = report.name;
                    this.componentRef.instance['report'] = report;
                    this.store.dispatch(disable_content_loader());
                } catch (e) {
                    this.store.dispatch(disable_content_loader());
                    console.error(`Module ${moduleName} doesn't exists`, e);
                    this.notificationService.error(e.statusText || `Module ${moduleName} doesn't exists`);
                }
                break;

            case 'ssrs':
                m = await this.lazyLoader.dynamicImportSsrsReportModule();
                ngModuleRef = createNgModule(m, this.injector);
                ReportComponent = m.getReportComponent();
                this.componentRef = this.currentReportViewRef.createComponent(ReportComponent, {
                    ngModuleRef: ngModuleRef,
                });
                this.componentRef.instance['report'] = report;
                this.componentRef.changeDetectorRef.detectChanges();
                break;

            case 'ag-grid':
                if (reportIdArr[reportIdArr.length - 1] === 'report') {
                    reportIdArr.pop();
                    reportName = reportIdArr.join('_');
                }
                moduleName = reportName
                    .replace('/', '_')
                    .split('_')
                    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                    .join('');
                this.store.dispatch(enable_content_loader());

                m = await this.lazyLoader.dynamicImportAgGridReportModule(moduleName);
                try {
                    ngModuleRef = createNgModule(m, this.injector);
                    await this.translations.use(`${moduleName}Module`).toPromise();
                    ReportComponent = m.getReportComponent();

                    if (report) {
                        this.componentRef = this.currentReportViewRef.createComponent(ReportComponent, {
                            ngModuleRef: ngModuleRef,
                        });
                        this.store.dispatch(disable_content_loader());
                        this.componentRef.instance['currentCompanyId'] = companyId;
                        this.componentRef.instance['currentCompanyInfo'] = companyInfo;
                        this.componentRef.instance['report'] = report;
                        this.componentRef.instance['drillDownMode'] = drillDownMode;
                    }
                } catch (e) {
                    this.store.dispatch(disable_content_loader());
                    console.error(`Module ${moduleName} doesn't exists`, e);
                    this.notificationService.error(e.statusText || `Module ${moduleName} doesn't exists`);
                }
                break;

            case 'ag-chart':
                if (reportIdArr[reportIdArr.length - 1] === 'report') {
                    reportIdArr.pop();
                    reportName = reportIdArr.join('_');
                }
                moduleName = reportName
                    .replace('/', '_')
                    .split('_')
                    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                    .join('');
                this.store.dispatch(enable_content_loader());
                m = await this.lazyLoader.dynamicImportAgChartReportModule(moduleName);
                try {
                    ngModuleRef = createNgModule(m, this.injector);
                    await this.translations.use(`${moduleName}Module`).toPromise();
                    ReportComponent = m.getReportComponent();
                    if (report) {
                        this.componentRef = this.currentReportViewRef.createComponent(ReportComponent, {
                            ngModuleRef: ngModuleRef,
                        });
                        this.store.dispatch(disable_content_loader());
                        this.componentRef.instance['currentCompanyId'] = companyId;
                        this.componentRef.instance['currentCompanyInfo'] = companyInfo;
                        this.componentRef.instance['report'] = report;
                    }
                } catch (e) {
                    this.store.dispatch(disable_content_loader());
                    console.error(`Module ${moduleName} doesn't exists`, e);
                    this.notificationService.error(e.statusText || `Module ${moduleName} doesn't exists`);
                }
        }
    }

    /*** Loading UI ag-grid reports that does not have report options ***/
    public async loadStandaloneAgGirdModule(
        reportName: string,
    ): Promise<{ component: ComponentRef<IReport>; ngModuleRef: NgModuleRef<any> }> {
        this.store.dispatch(enable_content_loader());
        const module = await this.lazyLoader.dynamicImportAgGridReportModule(reportName);
        try {
            const ngModuleRef = createNgModule(module, this.injector);
            await this.translations.use(`${reportName}Module`).toPromise();
            const ReportComponent: ComponentRef<IReport> = module.getReportComponent();
            this.store.dispatch(disable_content_loader());

            return {
                component: ReportComponent,
                ngModuleRef,
            };
        } catch (e) {
            this.store.dispatch(disable_content_loader());
            console.error(`Module ${reportName}Module doesn't exists`, e);
        }
    }

    public drillDownNavigation(destinationReportId: string, isDetail?: boolean, parent_custom_name?: string): void {
        if (this.filtersRef) {
            this.filtersRef.destroy();
        }
        this.router.navigate(['/custom-reports/report/', destinationReportId], {
            state: { autorun: true, drillDown: true, isDetail, parentCustomTitle: parent_custom_name },
        });
    }

    public isStandaloneRun(): boolean {
        return this.standaloneRun;
    }

    public isBreadcrumbRun(): boolean {
        return this.breadcrumbRun;
    }

    public resetMainFilters(): void {
        this.store
            .select(selectReportsHistory)
            .pipe(
                filter((history) => {
                    const firstOpenedReportHistory = history[1];
                    return !!firstOpenedReportHistory && history.length > 2;
                }),
                take(1),
                map((history) => history[1]),
                switchMap((history) =>
                    combineLatest([this.store.select(selectSiteState), this.store.select(selectEventState)]).pipe(
                        take(1),
                        tap(([siteState, eventState]) => {
                            this.calendarPickerService.resetDateMainFilter(history);
                            this.sitePickerService.resetSiteMainFilter(siteState, history);
                            this.eventPickerService.resetEventMainFilter(eventState, history, this.loadedReportId);
                        }),
                    ),
                ),
            )
            .subscribe();
    }

    public unloadReport(): void {
        this.loadedReportId = null;
        this.store.dispatch(close_active_report());
    }
}
